import { IconPencil, IconPlus, IconTrash } from '@tabler/icons-react';
import type React from 'react';
import { useMemo } from 'react';
import type { PartnerApiRow } from '@/api/partners';
import {
  type ColumnConfig,
  DataTable,
  type DataTableResponse,
  type FilterConfig,
} from '@/components/data-table';
import { PartnerStatusBadge } from '@/components/partners/partner-status-badge';
import type { RowActivatorLink } from '@/components/row-activator';
import { SearchInput } from '@/components/search-input';
import { Button } from '@/components/ui/button';
import { formatPartnerDate } from '@/schemas/partner-form-schema';

interface PartnersTableProps {
  data: DataTableResponse<PartnerApiRow> | undefined;
  isLoading: boolean;
  error: string | null;
  currentFilterValues: Record<string, string | string[]>;
  currentSortKey: string;
  currentSortOrder: 'asc' | 'desc';
  searchValue: string;
  onFilterChange: (filterKey: string, value: string | string[]) => void;
  onSortChange: (sortBy: string, order: 'asc' | 'desc') => void;
  onPageChange: (page: number) => void;
  onSearchChange: (value: string) => void;
  onAdd: () => void;
  onEdit: (row: PartnerApiRow) => void;
  onDelete: (row: PartnerApiRow) => void;
  onRowClick?: ((row: PartnerApiRow) => void) | undefined;
  /** Detail route for the row's keyboard activator; omit to fall back to a button. */
  getRowLink?: ((row: PartnerApiRow) => RowActivatorLink) | undefined;
}

const FILTERS: FilterConfig[] = [
  {
    key: 'status',
    label: 'Status',
    placeholder: 'All',
    options: [
      { value: 'active', label: 'Active' },
      { value: 'inactive', label: 'Inactive' },
    ],
  },
];

export const PartnersTable: React.FC<PartnersTableProps> = ({
  data,
  isLoading,
  error,
  currentFilterValues,
  currentSortKey,
  currentSortOrder,
  searchValue,
  onFilterChange,
  onSortChange,
  onPageChange,
  onSearchChange,
  onAdd,
  onEdit,
  onDelete,
  onRowClick,
  getRowLink,
}) => {
  const columns: ColumnConfig<PartnerApiRow>[] = useMemo(
    () => [
      {
        key: 'partner_name',
        label: 'Name',
        sortable: true,
        render: (item) => <span className="font-medium">{item.partner_name}</span>,
      },
      {
        key: 'partner_code',
        label: 'Code',
        sortable: true,
        render: (item) => <span className="font-mono text-sm">{item.partner_code}</span>,
      },
      {
        key: 'status_id',
        label: 'Status',
        sortable: false,
        render: (item) => <PartnerStatusBadge statusId={item.status_id} />,
      },
      {
        key: 'created_at',
        label: 'Created',
        sortable: true,
        align: 'right',
        render: (item) => (
          <span className="text-muted-foreground">
            {item.created_at ? formatPartnerDate(item.created_at) : '—'}
          </span>
        ),
      },
      {
        key: 'actions',
        label: 'Actions',
        sortable: false,
        align: 'center',
        render: (item) => (
          <div className="flex items-center justify-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              aria-label={`Edit ${item.partner_code}`}
              onClick={(e) => {
                e.stopPropagation();
                onEdit(item);
              }}
            >
              <IconPencil size={14} />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-destructive hover:text-destructive"
              aria-label={`Delete ${item.partner_code}`}
              onClick={(e) => {
                e.stopPropagation();
                onDelete(item);
              }}
            >
              <IconTrash size={14} />
            </Button>
          </div>
        ),
      },
    ],
    [onEdit, onDelete]
  );

  const searchFilter = (
    <SearchInput
      id="partners-search"
      className="w-[260px]"
      value={searchValue}
      onChange={onSearchChange}
      placeholder="Search by name or code…"
      hint="Search by name or code"
      sessionStorageKey="partners_recent_searches"
    />
  );

  return (
    <DataTable
      data={data}
      columns={columns}
      caption="Partners"
      filters={FILTERS}
      currentFilterValues={currentFilterValues}
      currentSortKey={currentSortKey}
      currentSortOrder={currentSortOrder}
      isLoading={isLoading}
      error={error}
      onFilterChange={onFilterChange}
      onSortChange={onSortChange}
      onPageChange={onPageChange}
      onRowClick={onRowClick}
      getRowLabel={(item) => `Open partner ${item.partner_name}`}
      {...(getRowLink && { getRowLink })}
      extraFilters={searchFilter}
      headerActions={
        <Button onClick={onAdd}>
          <IconPlus className="mr-2 h-4 w-4" />
          Add Partner
        </Button>
      }
      emptyMessage="No partners found"
      loadingMessage="Loading partners…"
      defaultSortKey="partner_name"
      defaultSortOrder="asc"
    />
  );
};
